import type { components } from "@/lib/api/generated";
import type { Override } from "@/shared/lib/typeOverride";
import { api } from "@/shared/api";

// local-only: A (frontend union); see proposals/2026-05-17-fe-generated-coverage-misc.md
export type KnowledgeDocType = "product" | "qa";

export type KnowledgeDocument = Override<
  components["schemas"]["KbDocumentDto"],
  {
    chunkCount: number | null;
    tags: string[];
    docType: KnowledgeDocType;
    createdAt: string;
    // V27 (2026-05-19) — true 时下载按钮 enable,false = V27 前老数据原文件不存
    originalAvailable: boolean;
    /**
     * D6 治理轴:pending = 传上去了但还没人批,客服**检索不到**。
     *
     * 与 `status`(ingest 管线:pending|processing|done|failed)是**两个轴**——一份文档可以
     * status=done 且 reviewStatus=pending(向量化好了,但没人批)。只看 status 会把"已完成"
     * 显示给一份对外根本不存在的文档。
     */
    reviewStatus?: "pending" | "approved" | "rejected";
    /** K-2 提名者自治:是否当前用户上传(自己的未过审文档可删/重传)。 */
    mine?: boolean;
    /**
     * 治理轴:给谁看。`public` = AI 客服/官网检索得到,`internal` = 仅内部。
     *
     * 与 `reviewStatus` **正交**,公开检索要求两根同时满足。2026-08-07 生产事故:某租户 19 份
     * 文档全是 done + approved、管理端一片绿,而客服一条都检索不到 —— 因为这一根全是 internal,
     * 而当时界面上根本没有它。只显示 reviewStatus 等于告诉租户"一切正常",这是骗人。
     */
    visibility?: "public" | "internal" | "restricted";
    /**
     * ingest 失败的具体原因(0816 反馈:列表只画「失败」不给原因,用户不知道能不能重传)。
     * 后端**仅 status=failed 时下发**(done 行的陈年残留不透传),所以拿到了就能直接展示。
     */
    errorMsg?: string | null;
    /**
     * 0926:失败原因码(api KbIngestFailure,如 AI_UNAVAILABLE / PARSE_EMPTY);`errorMsg` 是它对应的人话。
     * 旧 api / 存量原文行没有码或为 `UNKNOWN` → 界面用兜底句,**不渲染原文**(原文里曾带内部地址)。
     * 展示一律走 `kbFailureText()`,别直接画 `errorMsg`。
     */
    errorCode?: string | null;
    /**
     * 2026-09-07 — 个人知识库(一期,老板拍板)。`PERSONAL` = 员工自己传的私人文档,只在
     * `/tenant/knowledge/personal/*` 这组端点下可读写、且只对本人可见;`TENANT` = 走既有租户库
     * 治理链路(待审 → 审批 → 对客可见)的文档,即上面 `reviewStatus`/`visibility` 描述的那条路。
     *
     * ★个人文档 promote 成功后后端把它翻成 `TENANT` 并塞进待审队列 —— 从那一刻起它就从
     * `listPersonalDocuments()` 的结果里消失(后端按 scope 过滤),不再是「同一份文档换了个字段」
     * 意义上的个人文档。旧文档(V27 之前上传的租户库文档)没有这个字段,当 `undefined` 处理即
     * 等同 `TENANT`,别写成必填。
     */
    scope?: "PERSONAL" | "TENANT";
    /**
     * 2026-09-07 — 个人文档的所有者(登录 principal id)。只在 `scope === "PERSONAL"` 时有意义;
     * 前端**不用它做筛选**——`listPersonalDocuments()` 后端已经按登录身份过滤成"只含本人的",
     * 这个字段只是让详情/调试能核对"这份是不是我的"，不是第二套判据。
     */
    ownerPrincipalId?: number;
    /**
     * 2026-09-16 — **对内**可见范围:`ALL` 全员 / `DEPT` 指定部门及下级 / `ADMIN` 仅管理员 /
     * `OWNER` 仅本人。
     *
     * 与 `visibility` 是两根正交的轴:那根管「客户能不能看到」,这根管「公司内部谁能看到」。
     * 一份报价单可以 approved(可拿来答)+ internal(不对客)+ ADMIN(内部也只给管理员),
     * 三者互不蕴含 —— 所以列表上两个徽章并排,不是二选一。
     */
    visibleScope?: KbVisibleScope;
    /** `visibleScope === "DEPT"` 时的部门 id;拿它去已加载的部门树里取名,后端不为此多查一次。 */
    visibleDeptId?: number | null;
    /**
     * 这个人能不能下载本文档原件 = 有原件 ∧ 看得见 ∧ 过下载闸(租户可关掉一线的下载)。
     *
     * 后端下发布尔而不是让前端自己推:推的规则一旦和后端分叉,就是「按钮亮着却 403」
     * 或者「能下却灰着」。老数据/旧后端没有这个字段时回落 `originalAvailable`。
     */
    canDownload?: boolean;
    // mark: not yet in api-spec.yaml (V24 proposal)
  }
>;

/** 对内可见范围四档(2026-09-16)。值与后端 `KbVisibleScope` 枚举逐字对齐,别在别处另抄字面量。 */
export type KbVisibleScope = "ALL" | "DEPT" | "ADMIN" | "OWNER";

// local-only: B (backend schema gap) — POST /tenant/knowledge/upload 返
export type UploadKnowledgeResponse = {
  docId: string;
  chunksStored: number;
  // 后端同步透传 ai 状态:done | processing | duplicate | failed。
  // failed(如 doc_type=qa 但文件无 Q&A 对)时 error 带具体原因,前端据此报错。
  status?: string;
  error?: string | null;
  tags: string[];
  warnings?: string[];
};

// local-only: G3.3 — GET /tenant/knowledge/coverage(尚未进 api-spec.yaml,见 proposals/2026-07-16-kb-coverage-panel.md)
export type CoverageMissQuestion = { question: string; count: number };
export type CoverageDayStat = { date: string; hit: number; miss: number; hitRate: number };
export type CoverageStats = {
  startDate: string;
  endDate: string;
  days: number;
  hit: number;
  miss: number;
  hitRate: number; // 0~1
  topMissQuestions: CoverageMissQuestion[]; // 内容缺口:客户问了、KB 没答上,窗口内累计次数降序
  series: CoverageDayStat[]; // 逐日,时间序(旧→新)
};

export const knowledgeService = {
  listDocuments: () => api.get<KnowledgeDocument[]>("/tenant/knowledge/documents"),
  /**
   * G3.3 — KB coverage 面板数据(命中率 + 内容缺口)。默认近 7 天;单日计数到 midnight 清零
   * 对经理没用,故 backend/ai 侧做区间聚合。`days` 后端夹 [1,30]。
   */
  getCoverageStats: (days = 7) => api.get<CoverageStats>(`/tenant/knowledge/coverage?days=${days}`),
  /**
   * V27 (2026-05-20) — 上传 KB 文档。
   *
   * 后端政策:@PreAuthorize("hasRole('TENANT_MANAGER')")，真 TENANT_MANAGER 可直传，
   * 无 isImpersonating 门（平台超管代客时也走同一端点，被 JwtAuthFilter 追加 ROLE_TENANT_MANAGER 放过）。
   * tags 由调用方从已注册字典里挑(逗号分隔 tagValue), 后端会过滤掉字典里没有的,
   * 不会再调 LLM, 也不会自动建新 tag。
   */
  uploadDocument: (
    file: File,
    tags?: string,
    docType: KnowledgeDocType = "product",
    // 2026-06-04 — 可选「所属产品」节点 code(空 = 通用,不绑定产品树)。
    nodeCode?: string,
    // S3 闭环 — 可选源任务 id(copy/cs_kb「去做」存入时带,后端入库后绑回源任务推进 SUBMITTED)。
    sourceTaskId?: number | null,
    // 2026-09-16 — 对内可见范围。不传 = 跟渠道默认(租户上传恒 ALL),所以既有调用点零改动。
    visibleScope?: KbVisibleScope,
    visibleDeptId?: number | null
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    if (tags) formData.append("tags", tags);
    formData.append("docType", docType);
    if (nodeCode) formData.append("nodeCode", nodeCode);
    if (sourceTaskId) formData.append("sourceTaskId", String(sourceTaskId));
    if (visibleScope) formData.append("visibleScope", visibleScope);
    if (visibleScope === "DEPT" && visibleDeptId) {
      formData.append("visibleDeptId", String(visibleDeptId));
    }
    return api.post<UploadKnowledgeResponse>("/tenant/knowledge/upload", formData);
  },

  /**
   * 2026-09-16 — 改一份文档的对内可见范围。ADMIN 任意改,经理限自己看得见的文档;后端落审计。
   *
   * 与审批台的「批准 / 放开原件 / 对客可见」三个动作**并列但正交** —— 那三个管客户那一侧。
   */
  setVisibleScope: (id: number, visibleScope: KbVisibleScope, visibleDeptId?: number | null) =>
    api.put<KnowledgeDocument>(`/tenant/knowledge/documents/${id}/visible-scope`, {
      visibleScope,
      visibleDeptId: visibleScope === "DEPT" ? visibleDeptId : null,
    }),
  deleteDocument: (id: number) => api.delete<void>(`/tenant/knowledge/documents/${id}`),

  /**
   * 生成物回流:把一件海报/视频提名进 KB(E4 / design §8「军师产出 → 用户"这个好,入库"」)。
   *
   * **提名 ≠ 入库**:产物恒 pending,进不了 live 检索 —— 污染半径 0,所以租户员工就能提,不必代客态。
   * 真正的门在审批台(平台超管代客审)。
   *
   * **不发提名人**:后端从登录态取真实身份。四眼原则要证明 checker ≠ nominator,让调用方自称
   * "我是谁"等于把审计变成许愿池。
   *
   * **不重传字节**(§8「不重传,只多一个 source_type」):只发 (taskId, seq),后端让 ai 直接引用
   * gscq-media 桶里那件产物。重传一份意味着同一张图在两个桶各存一份、各自过期、各自被删 ——
   * 客服吐出来的那份和用户在海报页看到的那份迟早不是同一张。
   *
   * @returns 新建知识对象的 docId(pending,等审批台放行才进 live 检索)。
   *   ★后端 `KnowledgeController.nominateDeliverable` 返回的是 `ApiResponse.ok(doc.getId())`,
   *   即 `data` 是**裸 number**,不是对象 —— 别声明成 `{docId}` 然后读 `res.docId`(会拿到
   *   undefined)。第一版就是照着一个不存在的对象契约写的,靠自写的 MSW handler 才绿。
   */
  nominateDeliverable: (body: { taskId: number; seq: number }) =>
    api.post<number>("/tenant/knowledge/deliverables/nominate", body),
  /**
   * V27 (2026-05-19) — 下载原文件做内容校验。
   *
   * 后端从 MinIO `kb-originals` 桶取回字节流,租户员工以上都能用;
   * 平台超管代客时也走同一端点(被 JwtAuthFilter 追加的 ROLE_TENANT_ADMIN 放过 filter chain)。
   * V27 前上传的老数据 originalAvailable=false,UI 应禁用按钮。
   */
  downloadDocument: async (id: number, filename: string) => {
    const res = await api.getBlob(`/tenant/knowledge/documents/${id}/download`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  // ─── 个人知识库(2026-09-07,一期,老板拍板)─────────────────────────────────
  //
  // 与上面 uploadDocument/listDocuments/deleteDocument 是**平行的另一套端点**,不是同一批文档换
  // 个过滤条件:个人文档不进 knowledge_base.write 那套 MANAGER+ 门(员工本人即可传/删),也不经过
  // `KbDocReviewQueue` 的待审队列 —— 唯一的治理动作是下面的 promote(主动"共享到租户库",
  // 那才是它真正进待审队列的入口)。

  /**
   * 2026-09-07 — 上传个人文档。字段名 `file`(与 uploadDocument 的多字段表单不同,这里只有一个
   * 文件本身,不带 tags/docType/nodeCode —— 个人文档一律通用,不绑产品树)。
   *
   * 员工即可调(不需要 MANAGER+)。后端限制:单文件 ≤20MB、每人最多 20 份,超限时返回**人话**
   * message(不是字段级 422),前端直接把 `ApiError.message` 丢给 toast 展示,不用再翻译。
   */
  uploadPersonalDocument: (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post<KnowledgeDocument>("/tenant/knowledge/personal/upload", formData);
  },

  /** 2026-09-07 — 我的个人文档列表。后端按登录 principal 过滤,只含本人上传、scope=PERSONAL 的。 */
  listPersonalDocuments: () => api.get<KnowledgeDocument[]>("/tenant/knowledge/personal/documents"),

  /**
   * 2026-09-16 — 下载自己的个人文档原件。
   *
   * **不能复用上面的 `downloadDocument`**:共享库那个口按对内可见范围判权,个人档在它眼里是
   * `OWNER` 档、一律回 404(2026-09-16 之前它只校租户,任何员工凭自增 id 就能下走别人的私人文件)。
   */
  downloadPersonalDocument: async (id: number, filename: string) => {
    const res = await api.getBlob(`/tenant/knowledge/personal/documents/${id}/download`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  /** 2026-09-07 — 删除自己的个人文档。本人可删,不需要额外角色门(与租户库删除的多路径判定不同)。 */
  deletePersonalDocument: (id: number) =>
    api.delete<void>(`/tenant/knowledge/personal/documents/${id}`),

  /**
   * 2026-09-07 — 把个人文档「共享」到租户库:进现有待审队列(`KbDocReviewQueue` 那条治理链路),
   * **不是**直接对客/对内生效 —— 返回的文档 scope 已翻成 `TENANT`,reviewStatus 回到
   * `pending`,还要走平台审核才会被 AI 客服检索到。
   *
   * 成功后这份文档会从 `listPersonalDocuments()` 的结果里消失(后端按 scope 过滤),
   * 调用方应据此把它从本地个人列表移除,不要等下一次轮询。
   */
  promotePersonalDocument: (id: number) =>
    api.post<KnowledgeDocument>(`/tenant/knowledge/personal/documents/${id}/promote`, undefined),
};
